// src/pages/shop/ManualPoints.tsx

import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
} from "@mui/material";
import {
  Email as EmailIcon,
  Star as StarIcon,
  Note as NoteIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import rewardService from "../../services/reward.service";
import ProtectedLayout from "../../components/common/ProtectedLayout";
const ManualPoints: React.FC = () => {
  const { user } = useAuth();
  const shopId = user?.shopId;

  // Form állapotok
  const [email, setEmail] = useState("");
  const [points, setPoints] = useState<number | "">("");
  const [reason, setReason] = useState("");

  // UI állapotok
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{
    userName: string;
    pointsAdded: number;
    currentPoints: number;
  } | null>(null);

  // Form beküldése
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validáció
    if (!email.trim()) {
      setError("Az email cím megadása kötelező.");
      return;
    }

    if (!points || points === 0) {
      setError("A pontok számának nullától különbözőnek kell lennie.");
      return;
    }

    if (!shopId) {
      setError("Nincs bolt azonosító. Kérjük, jelentkezz be újra.");
      return;
    }

    try {
      setLoading(true);

      const response = await rewardService.addManualPoints(shopId, {
        email: email.trim(),
        points: Number(points),
        reason: reason.trim() || undefined,
      });

      setSuccess({
        userName: response.user.name,
        pointsAdded: response.pointsAdded,
        currentPoints: response.currentPoints,
      });

      // Form ürítése sikeres beküldés után
      setEmail("");
      setPoints("");
      setReason("");
    } catch (err: any) {
      setError(
        err.response?.data?.msg || "Hiba történt a pont jóváírás során."
      );
    } finally {
      setLoading(false);
    }
  };

  // Form törlése
  const handleReset = () => {
    setEmail("");
    setPoints("");
    setReason("");
    setError(null);
    setSuccess(null);
  };

  return (
    <ProtectedLayout>
      <Box sx={{ maxWidth: 600, mx: "auto", p: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Manuális pont jóváírás
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Írj jóvá pontokat egy vásárlónak az email címe alapján.
        </Typography>

        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          {/* Sikeres üzenet */}
          {success && (
            <Alert
              severity="success"
              icon={<CheckCircleIcon />}
              sx={{ mb: 3 }}
              onClose={() => setSuccess(null)}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                {success.pointsAdded > 0
                  ? "Pont jóváírás sikeres!"
                  : "Pont levonás sikeres!"}
              </Typography>
              <Typography variant="body2">
                {success.userName} új egyenlege:{" "}
                <strong>{success.currentPoints} pont</strong>
                {success.pointsAdded > 0
                  ? ` (+${success.pointsAdded})`
                  : ` (${success.pointsAdded})`}
              </Typography>
            </Alert>
          )}

          {/* Hiba üzenet */}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {/* Email mező */}
            <TextField
              fullWidth
              label="Vásárló email címe"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="pelda@email.com"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
              disabled={loading}
              required
            />

            {/* Pontok mező */}
            <TextField
              fullWidth
              label="Pontok száma"
              type="number"
              value={points}
              onChange={(e) =>
                setPoints(e.target.value ? Number(e.target.value) : "")
              }
              placeholder="100"
              helperText="Pozitív szám: jóváírás, negatív szám: levonás"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <StarIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
              disabled={loading}
              required
            />

            {/* Megjegyzés mező */}
            <TextField
              fullWidth
              label="Megjegyzés (opcionális)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Pl.: Kompenzáció, promóció, stb."
              multiline
              rows={2}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <NoteIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
              disabled={loading}
            />

            <Divider sx={{ my: 2 }} />

            {/* Gombok */}
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={loading}
              >
                Törlés
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || !email || !points}
                startIcon={
                  loading ? <CircularProgress size={20} /> : <StarIcon />
                }
              >
                {loading ? "Feldolgozás..." : "Pont jóváírás"}
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>
    </ProtectedLayout>
  );
};

export default ManualPoints;
